import React, { useState, useMemo } from 'react';
import { MapPin, Phone, User, Search, X, DollarSign, PlusCircle } from 'lucide-react';
import {
  getDeliveryPricingInfo,
  hasDeliveryAddons,
  isSpecialTariffDelivery,
} from './pricingIntelligence';

function DeliveryPointsList({ deliveryPoints, onItemClick, activeFilter: controlledActiveFilter, onFilterChange }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [internalActiveFilter, setInternalActiveFilter] = useState('all'); // 'all', 'pending', 'encamino', 'entregado', 'cancelado', 'rechazado', 'postergado', 'special-tariff', 'with-addons'

  const activeFilter = controlledActiveFilter ?? internalActiveFilter;
  const setActiveFilter = onFilterChange ?? setInternalActiveFilter;

  // Filter delivery points based on search term and active filter
  const filteredDeliveryPoints = useMemo(() => {
    let filtered = deliveryPoints;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      
      filtered = filtered.filter((dp) => {
        // Search by ID
        if (dp.id.toString().includes(search)) return true;
        
        // Search by recipient name
        if (dp.recipient_name?.toLowerCase().includes(search)) return true;
        
        // Search by recipient phone
        if (dp.recipient_phone?.includes(search)) return true;
        
        // Search by driver name
        if (dp.driver?.username?.toLowerCase().includes(search)) return true;
        
        // Search by address
        if (dp.address?.toLowerCase().includes(search)) return true;

        const pricing = getDeliveryPricingInfo(dp);
        if (pricing.serviceLabel?.toLowerCase().includes(search)) return true;
        if (pricing.totalLabel?.toLowerCase().includes(search)) return true;
        if (pricing.addonLabels.some(label => label.toLowerCase().includes(search))) return true;
        
        return false;
      });
    }

    // Apply status filter
    if (activeFilter !== 'all') {
      if (activeFilter === 'special-tariff') {
        filtered = filtered.filter(isSpecialTariffDelivery);
      } else if (activeFilter === 'with-addons') {
        filtered = filtered.filter(hasDeliveryAddons);
      } else {
        filtered = filtered.filter(dp => dp.status === activeFilter);
      }
    }

    return filtered;
  }, [deliveryPoints, searchTerm, activeFilter]);

  // Calculate counts for each filter
  const getFilterCounts = useMemo(() => {
    const counts = {
      all: deliveryPoints.length,
      pending: 0,
      encamino: 0,
      entregado: 0,
      cancelado: 0,
      rechazado: 0,
      postergado: 0,
      'special-tariff': 0,
      'with-addons': 0,
    };

    deliveryPoints.forEach(dp => {
      if (counts[dp.status] !== undefined) {
        counts[dp.status]++;
      }
      if (isSpecialTariffDelivery(dp)) counts['special-tariff']++;
      if (hasDeliveryAddons(dp)) counts['with-addons']++;
    });

    return counts;
  }, [deliveryPoints]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      encamino: 'bg-blue-100 text-blue-800 border-blue-300',
      entregado: 'bg-green-100 text-green-800 border-green-300',
      cancelado: 'bg-gray-100 text-gray-800 border-gray-300',
      rechazado: 'bg-red-100 text-red-800 border-red-300',
      postergado: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getPackageStatusColor = (status) => {
    const colors = {
      in_warehouse: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      out_for_delivery: 'bg-blue-100 text-blue-800 border-blue-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      lost_or_damaged: 'bg-gray-100 text-gray-800 border-gray-300',
      returned: 'bg-red-100 text-red-800 border-red-300',
      in_fastmax: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En camino',
      entregado: 'Entregado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
      postergado: 'Postergado',
      'special-tariff': 'Tarifa especial',
      'with-addons': 'Con addons',
    };
    return labels[status] || status;
  };

  const getPackageStatusLabel = (status) => {
    const labels = {
      in_warehouse: 'En tienda',
      in_fastmax: 'En Fast Max',
      out_for_delivery: 'En reparto',
      delivered: 'Entregado',
      returned: 'Devuelto',
      lost_or_damaged: 'Perdido/Dañado',
    };
    return labels[status] || status;
  };

  const truncateName = (name, maxLength = 15) => {
    if (!name) return 'Sin nombre';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActiveFilter('all');
  };

  // Define filter chips
  const filters = [
    { id: 'all', label: 'Todos', color: 'slate' },
    { id: 'special-tariff', label: 'Tarifa especial', color: 'amber' },
    { id: 'with-addons', label: 'Addons', color: 'purple' },
    { id: 'pending', label: 'Pendiente', color: 'yellow' },
    { id: 'encamino', label: 'En Camino', color: 'blue' },
    { id: 'entregado', label: 'Entregado', color: 'green' },
    { id: 'postergado', label: 'Postergado', color: 'orange' },
    { id: 'cancelado', label: 'Cancelado', color: 'gray' },
    { id: 'rechazado', label: 'Rechazado', color: 'red' },
  ];

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white px-3 py-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800 font-montserrat">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#B1336E]/10">
              <MapPin className="w-4 h-4 text-[#B1336E]" />
            </span>
            Puntos de Entrega ({filteredDeliveryPoints.length})
          </h2>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white py-2 pl-10 pr-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#B1336E] font-montserrat"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Results count when searching or filtering */}
        {(searchTerm || activeFilter !== 'all') && (
          <p className="mt-2 text-xs text-slate-500 font-montserrat">
            {filteredDeliveryPoints.length} resultado{filteredDeliveryPoints.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Filter Chips */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {filters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors font-montserrat ${
                activeFilter === filter.id
                  ? 'bg-[#B1336E] text-white'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              {filter.label} ({getFilterCounts[filter.id]})
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredDeliveryPoints.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <MapPin className="w-12 h-12 text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm font-montserrat">
              {searchTerm 
                ? 'No se encontraron entregas con ese criterio'
                : activeFilter !== 'all'
                ? `No hay entregas con estado "${getStatusLabel(activeFilter)}"`
                : 'No hay entregas para esta fecha'
              }
            </p>
            {(searchTerm || activeFilter !== 'all') && (
              <button
                onClick={handleClearFilters}
                className="mt-3 text-[#B1336E] hover:text-[#B1336E]/80 text-sm font-medium font-montserrat"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 p-2">
            {filteredDeliveryPoints.map((dp) => (
              (() => {
                const pricing = getDeliveryPricingInfo(dp);

                return (
              <button
                key={dp.id}
                onClick={() => onItemClick(dp.id)}
                className={`w-full rounded-md border bg-white p-3 text-left transition-all hover:border-[#B1336E]/30 hover:bg-slate-50 hover:shadow-sm ${
                  pricing.isSpecialTariff ? 'border-amber-300 ring-1 ring-amber-100' : 'border-slate-200'
                }`}
              >
                {/* ID and Status */}
                <div className="mb-2 flex items-start justify-between gap-2">
                  {/* Left side - ID and Sequence */}
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="text-[#B1336E] font-bold text-sm font-montserrat">
                      #{dp.id}
                    </span>
                    {dp.sequence && (
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700 font-montserrat">
                        Seq: {dp.sequence}
                      </span>
                    )}
                    {pricing.isSpecialTariff && (
                      <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 font-montserrat">
                        <DollarSign className="h-3 w-3" />
                        {pricing.serviceLabel} {pricing.totalLabel}
                      </span>
                    )}
                    {pricing.hasAddons && (
                      <span className="inline-flex items-center gap-1 rounded border border-purple-300 bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-800 font-montserrat">
                        <PlusCircle className="h-3 w-3" />
                        {pricing.addonCount} addon{pricing.addonCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  {/* Right side - Status Badges */}
                  <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {/* Delivery Status */}
                    <span
                      className={`whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium font-montserrat ${getStatusColor(
                        dp.status
                      )}`}
                    >
                      {getStatusLabel(dp.status)}
                    </span>
                    
                    {/* Package Status - Only show if exists */}
                    {dp.package_status && (
                      <span
                        className={`whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium font-montserrat ${getPackageStatusColor(
                          dp.package_status
                        )}`}
                      >
                        {getPackageStatusLabel(dp.package_status)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="min-w-0 truncate text-sm font-medium text-slate-800 font-montserrat">
                    {truncateName(dp.recipient_name)}
                  </span>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-600 text-xs font-montserrat">
                    {dp.recipient_phone || 'Sin teléfono'}
                  </span>
                </div>

                {/* Driver Info */}
                {dp.driver_id && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500 font-montserrat">
                      Conductor: {dp.driver?.username || `ID ${dp.driver_id}`}
                    </span>
                  </div>
                )}

                {pricing.isSpecialTariff && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
                    <div className="flex items-center justify-between gap-2 text-[11px] font-bold text-amber-800 font-montserrat">
                      <span>{pricing.badgeLabel}</span>
                      <span>{pricing.totalLabel}</span>
                    </div>
                    {pricing.addonLabels.length > 0 && (
                      <p className="mt-1 truncate text-[10px] font-medium text-purple-700 font-montserrat">
                        Addons: {pricing.addonLabels.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* Slot Indicator */}
                {dp.slot && (
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-xs text-slate-500 font-montserrat">Prioridad:</span>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        dp.slot === 1
                          ? 'bg-red-600'
                          : dp.slot === 2
                          ? 'bg-orange-500'
                          : dp.slot === 3
                          ? 'bg-yellow-500'
                          : dp.slot === 4
                          ? 'bg-lime-400'
                          : 'bg-yellow-200'
                      }`}
                    />
                  </div>
                )}
              </button>
                );
              })()
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeliveryPointsList;
